import { useState, useMemo, useEffect } from 'react';
import { ALL_CUSTOMER_STATUSES } from '../models/Client';

const buildStatusesParam = (selectedStatuses) => {
  if (selectedStatuses.size === 0) return undefined;
  if (selectedStatuses.size === ALL_CUSTOMER_STATUSES.length) return undefined;
  return [...selectedStatuses];
};

const buildCitiesParam = (selectedCities, totalCities) => {
  if (selectedCities.size === 0) return undefined;
  if (totalCities > 0 && selectedCities.size === totalCities) return undefined;
  return [...selectedCities];
};

export const CLIENT_LIST_PAGE_SIZES = [20, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 20;

export const useClientListFilters = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortConfig, setSortConfig] = useState({ field: 'companyName', direction: 'asc' });
  const [selectedStatuses, setSelectedStatuses] = useState(() => new Set());
  const [selectedCities, setSelectedCities] = useState(() => new Set());
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [cityFilterAnchorEl, setCityFilterAnchorEl] = useState(null);
  const [availableCitiesCount, setAvailableCitiesCount] = useState(0);

  const statusesParam = useMemo(
    () => buildStatusesParam(selectedStatuses),
    [selectedStatuses]
  );

  const citiesParam = useMemo(
    () => buildCitiesParam(selectedCities, availableCitiesCount),
    [selectedCities, availableCitiesCount]
  );

  const filterParams = useMemo(
    () => ({
      search: appliedSearch || undefined,
      statuses: statusesParam,
      cities: citiesParam,
    }),
    [appliedSearch, statusesParam, citiesParam]
  );

  const listParams = useMemo(
    () => ({
      page,
      size: pageSize,
      ...filterParams,
      sort: sortConfig,
    }),
    [page, pageSize, filterParams, sortConfig]
  );

  useEffect(() => {
    setPage(0);
  }, [statusesParam, citiesParam, sortConfig.field, sortConfig.direction]);

  const allStatusesSelected = selectedStatuses.size === ALL_CUSTOMER_STATUSES.length;
  const hasStatusFilter = selectedStatuses.size > 0;
  const allCitiesSelected = availableCitiesCount > 0 && selectedCities.size === availableCitiesCount;
  const hasCityFilter = selectedCities.size > 0;
  const hasActiveFilters = Boolean(appliedSearch) || hasStatusFilter || hasCityFilter;

  const handleSearch = () => {
    setAppliedSearch(searchTerm.trim());
    setPage(0);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setAppliedSearch('');
    setPage(0);
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleToggleStatus = (status) => {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  const handleSelectAllStatuses = () => {
    setSelectedStatuses(new Set(ALL_CUSTOMER_STATUSES));
  };

  const handleClearAllStatuses = () => {
    setSelectedStatuses(new Set());
  };

  const handleToggleCity = (city) => {
    setSelectedCities((prev) => {
      const next = new Set(prev);
      if (next.has(city)) {
        next.delete(city);
      } else {
        next.add(city);
      }
      return next;
    });
  };

  const handleSelectAllCities = (cities) => {
    setSelectedCities(new Set(cities.map((item) => item.city)));
  };

  const handleClearAllCities = () => {
    setSelectedCities(new Set());
  };

  const setCityOptionsCount = (count) => {
    setAvailableCitiesCount(count);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage - 1);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(Number(newPageSize));
    setPage(0);
  };

  const resetSearch = () => {
    setSearchTerm('');
    setAppliedSearch('');
    setPage(0);
  };

  return {
    searchTerm,
    setSearchTerm,
    appliedSearch,
    page,
    pageSize,
    sortConfig,
    selectedStatuses,
    selectedCities,
    filterAnchorEl,
    setFilterAnchorEl,
    cityFilterAnchorEl,
    setCityFilterAnchorEl,
    filterOpen: Boolean(filterAnchorEl),
    cityFilterOpen: Boolean(cityFilterAnchorEl),
    allStatusesSelected,
    allCitiesSelected,
    hasStatusFilter,
    hasCityFilter,
    hasActiveFilters,
    statusesParam,
    citiesParam,
    listParams,
    mapFilterParams: filterParams,
    handleSearch,
    handleClearSearch,
    handleSort,
    handleToggleStatus,
    handleSelectAllStatuses,
    handleClearAllStatuses,
    handleToggleCity,
    handleSelectAllCities,
    handleClearAllCities,
    setCityOptionsCount,
    handlePageChange,
    handlePageSizeChange,
    resetSearch,
    resetPage: () => setPage(0),
  };
};
